import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtPrice, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Smartphone, Banknote, Truck, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { t } = useLang();
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [payment, setPayment] = useState("mobile_money");
  const [addr, setAddr] = useState({ full_name: user?.name || "", phone: user?.phone || "", city: "Lomé", district: "", street: "" });
  const [mmNumber, setMmNumber] = useState("");
  const submittedRef = useRef(false);

  useEffect(() => { if (user === null) openAuth("login"); }, [user]); // eslint-disable-line
  useEffect(() => { if (items.length === 0 && !submittedRef.current) navigate("/cart"); }, [items.length]); // eslint-disable-line

  const shippingFee = shippingMethod === "express" ? 2500 : 1000;
  const total = subtotal + shippingFee;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { openAuth("login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address: addr, shipping_method: shippingMethod,
        payment_method: payment, payment_phone: mmNumber,
      });
      submittedRef.current = true;
      navigate(`/order-success/${data.id}`, { state: { order: data } });
      setTimeout(() => clear(), 50);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const upd = (k, v) => setAddr((a) => ({ ...a, [k]: v }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8">{t("checkout")}</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-8">
          {/* Address */}
          <section className="bg-white border border-stone-200 rounded-xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">1. {t("delivery_address")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">{t("full_name")}</Label><Input value={addr.full_name} onChange={(e) => upd("full_name", e.target.value)} required data-testid="addr-name" /></div>
              <div><Label className="text-xs">{t("phone")}</Label><Input value={addr.phone} onChange={(e) => upd("phone", e.target.value)} required data-testid="addr-phone" /></div>
              <div><Label className="text-xs">{t("city")}</Label><Input value={addr.city} onChange={(e) => upd("city", e.target.value)} required data-testid="addr-city" /></div>
              <div><Label className="text-xs">{t("district")}</Label><Input value={addr.district} onChange={(e) => upd("district", e.target.value)} data-testid="addr-district" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">{t("street")}</Label><Input value={addr.street} onChange={(e) => upd("street", e.target.value)} data-testid="addr-street" /></div>
            </div>
          </section>

          {/* Shipping */}
          <section className="bg-white border border-stone-200 rounded-xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">2. {t("shipping_method")}</h2>
            <RadioGroup value={shippingMethod} onValueChange={setShippingMethod} className="grid sm:grid-cols-2 gap-3">
              {[
                { v: "standard", icon: Truck, label: t("standard"), desc: "2-4 jours", fee: 1000 },
                { v: "express", icon: Zap, label: t("express"), desc: "24h", fee: 2500 },
              ].map((o) => (
                <label key={o.v} data-testid={`ship-${o.v}`} className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${shippingMethod === o.v ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                  <RadioGroupItem value={o.v} />
                  <o.icon className="w-5 h-5 text-emerald-800" />
                  <div className="flex-1"><p className="font-medium text-sm">{o.label}</p><p className="text-xs text-stone-500">{o.desc}</p></div>
                  <span className="font-semibold text-sm">{fmtPrice(o.fee)}</span>
                </label>
              ))}
            </RadioGroup>
          </section>

          {/* Payment */}
          <section className="bg-white border border-stone-200 rounded-xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">3. {t("payment_method")}</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
              <label data-testid="pay-mobile_money" className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${payment === "mobile_money" ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                <RadioGroupItem value="mobile_money" />
                <Smartphone className="w-5 h-5 text-mango-600" />
                <div className="flex-1"><p className="font-medium text-sm">{t("mobile_money")}</p><p className="text-xs text-stone-500">Flooz · T-Money</p></div>
              </label>
              {payment === "mobile_money" && (
                <div className="ml-8"><Label className="text-xs">{t("mm_number")}</Label><Input value={mmNumber} onChange={(e) => setMmNumber(e.target.value)} placeholder="+228 ..." data-testid="mm-number" /></div>
              )}
              <label data-testid="pay-cash" className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${payment === "cash" ? "border-emerald-800 bg-emerald-50" : "border-stone-300"}`}>
                <RadioGroupItem value="cash" />
                <Banknote className="w-5 h-5 text-emerald-800" />
                <div className="flex-1"><p className="font-medium text-sm">{t("cash")}</p></div>
              </label>
            </RadioGroup>
          </section>
        </div>

        {/* Summary */}
        <div className="bg-white border border-stone-200 rounded-xl p-6 h-fit sticky top-24">
          <h2 className="font-display font-bold text-lg mb-4">{t("order_summary")}</h2>
          <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm"><span className="text-stone-600 line-clamp-1 pr-2">{i.quantity}× {i.name}</span><span className="font-medium shrink-0">{fmtPrice(i.price * i.quantity)}</span></div>
            ))}
          </div>
          <div className="border-t border-stone-200 pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">{t("subtotal")}</span><span>{fmtPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">{t("shipping")}</span><span>{fmtPrice(shippingFee)}</span></div>
            <div className="flex justify-between text-base pt-2 border-t border-stone-200"><span className="font-semibold">{t("total")}</span><span className="font-display font-extrabold text-emerald-800">{fmtPrice(total)}</span></div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-mango-600 hover:bg-mango-700 h-12 mt-5" data-testid="place-order-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("place_order")}
          </Button>
        </div>
      </form>
    </div>
  );
}
